import { type DesignSourceEnum, InsightsApi, PlatformEnum } from '@insights/insights-data-provider'
import { queryOptions } from '@tanstack/react-query'

import { AnalyticsManager } from '@inter-ikea-kompis/analytics'
import { CookieConsentService } from '@inter-ikea-kompis/services'
import type { IDexfSettings } from '@inter-ikea-kompis/types'

import { kioskIntegration } from '@/integrations/kiosk'
import { APP_ID } from '@/integrations/kompis/constants'
import { getCountryFromPath, getLanguageFromPath, getLocaleFromPath } from '@/utils/base-path-params'

export function initAnalyticsQueryOptions(dexfSettings: IDexfSettings, computerId?: string, storeId?: string) {
  return queryOptions({
    queryFn: async () => await connectApplication(dexfSettings, computerId, storeId),
    queryKey: ['init-insights', getLocaleFromPath()],
    staleTime: Infinity,
  })
}

export function initPlanningSessionQueryOptions(
  insightsApi: InsightsApi | null,
  designId: string,
  designSource: DesignSourceEnum,
) {
  return queryOptions({
    queryFn: async () => {
      await insightsApi?.initPlanningSession(designId, designSource, {}, designId)
      return null
    },
    queryKey: ['init-planning-session'],
    staleTime: Infinity,
  })
}

async function checkCookieConsent(dexfSettings: IDexfSettings) {
  if (!import.meta.env.PROD) {
    // Cookie consent is not relevant outside of production.
    return true
  }
  if (kioskIntegration.getKioskPlatform()) return true // It is always allowed to track when in kiosk

  try {
    const service = new CookieConsentService({
      locale: getLocaleFromPath(),
      settings: dexfSettings,
    })
    const consentLevel = 2

    return await service.hasCookieConsent(consentLevel)
  } catch {
    // Default to false if there is an error.
    return false
  }
}

async function connectApplication(dexfSettings: IDexfSettings, computerId?: string, storeId?: string) {
  const hasCookieConsent = await checkCookieConsent(dexfSettings)
  const isKiosk = !!kioskIntegration.getKioskPlatform()

  if (!hasCookieConsent) return null

  const insightsApi = new InsightsApi()
  AnalyticsManager.setInsightsApi(insightsApi)

  await insightsApi.connectApplication({
    applicationId: APP_ID,
    applicationVersion: import.meta.env.VITE_APP_VERSION ?? 'dev',
    countryCode: getCountryFromPath(),
    dev: import.meta.env.MODE !== 'production',
    isPlanner: false,
    kiosk: isKiosk,
    kioskId: computerId,
    languageCode: getLanguageFromPath(),
    platform: isKiosk ? PlatformEnum.kiosk : PlatformEnum.owfe,
    storeId,
  })

  return insightsApi
}
